import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import {
  fetchResultsData,
  parseCSVWithWorker,
} from "../features/simulations/components/FileUpload/ResultsDisplay";
import { useNavigation } from "./NavigationContext";
import { formatSimulationStamp } from "../features/simulations/utils/simulationDate";

const SimulationDataContext = createContext();

export const useSimulationDataContext = () => {
  const context = useContext(SimulationDataContext);
  if (!context) {
    throw new Error(
      "useSimulationDataContext must be used within a SimulationDataProvider",
    );
  }
  return context;
};

export const SimulationDataProvider = ({ children }) => {
  const {
    timeSeriesFileName,
    storageAccountName,
    selectedMicrogrid: simulationName,
  } = useNavigation();
  const [dailyData, setDailyData] = useState([]);
  const [allTimeData, setAllTimeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState(null);

  /* Reads a value through the platform Date parser and back out as wall
   * clock. Only safe for shapes that parse as LOCAL time — anything the
   * platform reads as UTC comes back a day out in a negative-offset zone,
   * which is why this is the fallback and not the main path. */
  const fromPlatformDate = (value) => {
    const parsedDate = new Date(value);
    if (isNaN(parsedDate)) return null;

    const yyyy = parsedDate.getFullYear();
    const mm = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const dd = String(parsedDate.getDate()).padStart(2, "0");
    const hh = String(parsedDate.getHours()).padStart(2, "0");
    const min = String(parsedDate.getMinutes()).padStart(2, "0");
    const sec = String(parsedDate.getSeconds()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${sec}`;
  };

  /* Rewrites the Date column to "YYYY-MM-DD HH:mm:ss" for every consumer
   * downstream. Result stamps are wall-clock simulation time carrying no zone,
   * so the normalisation is pure string work: routing "2024-01-01" through
   * new Date() read it as UTC midnight and wrote it back through the local
   * getters as 2023-12-31 — a reading moved a day, and at a year boundary a
   * whole year, out of place. See features/simulations/utils/simulationDate.js.
   *
   * The platform parser survives only as a fallback, for numbers and for text
   * shapes the parser does not claim ("Jan 1, 2024"), all of which it reads as
   * local time and so cannot shift.
   *
   * A string nothing can read is passed through untouched rather than blanked.
   * "" is a well-formed absence that every consumer downstream accepts without
   * complaint; the raw stamp is at least legible to whoever is reading the
   * column and fails where a person can diagnose it. Null and undefined still
   * become "", because consumers slice into this value and a non-string throws. */
  const toDateTimeWithSpace = (dateTimeStr) => {
    if (!dateTimeStr) return "";

    const normalised = formatSimulationStamp(dateTimeStr);
    if (normalised) return normalised;

    const fallback = fromPlatformDate(dateTimeStr);
    if (fallback) return fallback;

    return typeof dateTimeStr === "string" ? dateTimeStr : "";
  };

  const transformData = (chunk) => {
    return chunk.map((entry) => ({
      ...entry,
      Date: toDateTimeWithSpace(entry.Date),
    }));
  };

  const processInChunks = async (data, chunkSize = 1000) => {
    const results = [];
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      await new Promise((resolve) => {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            results.push(...transformData(chunk));
            resolve();
          });
        } else {
          setTimeout(() => {
            results.push(...transformData(chunk));
            resolve();
          }, 0);
        }
      });
    }
    return results;
  };

  /* Monotonic ticket for the in-flight load. Every response races the ones
   * issued after it: fetchResultsData, parseCSVWithWorker and processInChunks
   * each await, and processInChunks yields to requestIdleCallback once per
   * 1000 rows, so a large file selected first routinely lands after a small
   * one selected second. The effect below re-fires on every
   * timeSeriesFileName change, so the two overlap and settle in completion
   * order rather than issue order.
   *
   * The rows carry no simulation identity, so a superseded response that
   * reaches setDailyData renders the previous run's numbers under the newly
   * selected run's name, with isLoading already false and nothing on screen
   * to say so. Each call takes a ticket on entry and every setState checks
   * it, so a superseded run finishes its work and writes nothing — its error
   * and its finally block included, since either would otherwise clobber
   * state that belongs to the request that replaced it. */
  const requestSeq = useRef(0);

  const fetchSimulationData = useCallback(async () => {
    const seq = (requestSeq.current += 1);
    const isCurrent = () => requestSeq.current === seq;

    const token = sessionStorage.getItem("accessToken");
    const baseUrl = process.env.REACT_APP_BASE_URL;

    try {
      setIsLoading(true);
      setLoadingMessage("Fetching timeseries data...");
      setError(null);

      const isValidDataset =
        !!timeSeriesFileName &&
        timeSeriesFileName !== "0" &&
        timeSeriesFileName !== "null" &&
        timeSeriesFileName !== "undefined";

      if (!token || !baseUrl || !storageAccountName || !simulationName || !isValidDataset) {
        throw new Error(
          "Missing selection/auth state (token, storage account, microgrid, or timeseries file). Please re-select a microgrid/dataset or log in again.",
        );
      }

      const csvText = await fetchResultsData(
        "Timeseries",
        timeSeriesFileName,
        baseUrl,
        storageAccountName,
        simulationName,
        token,
      );

      if (!isCurrent()) return;

      if (!csvText || csvText.trim() === "") {
        throw new Error("No CSV data found.");
      }

      setLoadingMessage("Parsing CSV data...");
      const parsed = await parseCSVWithWorker(csvText);
      if (!isCurrent()) return;

      setLoadingMessage("Processing data...");
      const transformed = await processInChunks(parsed.data);
      if (!isCurrent()) return;

      setDailyData(transformed);
      setAllTimeData(transformed);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err?.message || String(err));
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
        setLoadingMessage("");
      }
    }
  }, [timeSeriesFileName, storageAccountName, simulationName]);

  useEffect(() => {
    const isValid =
      !!timeSeriesFileName &&
      timeSeriesFileName !== "0" &&
      timeSeriesFileName !== "null" &&
      timeSeriesFileName !== "undefined";

    if (isValid) {
      fetchSimulationData();
    }
  }, [timeSeriesFileName, fetchSimulationData]);

  const contextValue = useMemo(
    () => ({
      dailyData,
      allTimeData,
      isLoading,
      loadingMessage,
      error,
      refetchData: fetchSimulationData,
    }),
    [
      dailyData,
      allTimeData,
      isLoading,
      loadingMessage,
      error,
      fetchSimulationData,
    ],
  );

  return (
    <SimulationDataContext.Provider value={contextValue}>
      {children}
    </SimulationDataContext.Provider>
  );
};
